/**
 * =========================================================
 * AUTH STORE - Zustand (Global State Management)
 * =========================================================
 * อัลกอริทึม Zustand:
 * 1. สร้าง store (singleton) ที่เก็บ state ทั้งหมด
 * 2. ใช้ hooks แบบ selector ให้ component ดึงเฉพาะที่ต้องการ
 * 3. ไม่ต้องใช้ Context Provider (เบากว่า Redux)
 * 4. Persist middleware → เก็บ state ลง localStorage
 * =========================================================
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

// ตั้งค่า axios base URL — ใช้ worker URL เมื่อ deploy แบบแยก frontend/worker
const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://lms-workers.glock000jjjjj.workers.dev';
axios.defaults.baseURL = apiBaseUrl;

// ถ้ามี token → ใส่ใน headers ทุก request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - ถ้า 401 → logout
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (username, password) => {
        set({ isLoading: true, error: null });
        try {
          const res = await axios.post('/auth/login', { username, password });
          const { user, token, refreshToken } = res.data.data;
          
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);
          
          set({ user, token, isAuthenticated: true, isLoading: false });
          return true;
        } catch (err) {
          set({ error: err.response?.data?.error || 'Login failed', isLoading: false });
          return false;
        }
      },

      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const res = await axios.post('/auth/register', userData);
          const { user, token, refreshToken } = res.data.data;
          
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);
          
          set({ user, token, isAuthenticated: true, isLoading: false });
          return true;
        } catch (err) {
          set({ error: err.response?.data?.error || 'Registration failed', isLoading: false });
          return false;
        }
      },

      logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        set({ user: null, token: null, isAuthenticated: false, error: null });
      },

      fetchUser: async () => {
        try {
          const res = await axios.get('/auth/me');
          set({ user: res.data.data, isAuthenticated: true });
        } catch (err) {
          set({ user: null, isAuthenticated: false });
        }
      }
    }),
    {
      name: 'auth-storage', // localStorage key
      partialize: (state) => ({ user: state.user, token: state.token, isAuthenticated: state.isAuthenticated }),
    }
  )
);
